import { useState, useEffect } from 'react';
import { CATEGORICAL, INK } from './palette';

const TAU = Math.PI * 2;

function arcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const p = (r, a) => [cx + r * Math.sin(a), cy - r * Math.cos(a)];
  const [x1, y1] = p(rOuter, startAngle);
  const [x2, y2] = p(rOuter, endAngle);
  const [x3, y3] = p(rInner, endAngle);
  const [x4, y4] = p(rInner, startAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${x1} ${y1}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

// Donut ("circle") chart - each slice grows in on mount (sweep animation)
// and pops outward along its own bisector when clicked, toggling a center
// label with that slice's name/value/share. A legend chip row is always
// shown (never color-alone identity) with the active slice bolded.
export default function AnimatedDonutChart({ data, valueKey = 'value', labelKey = 'label', size = 220, formatValue = (v) => v }) {
  const [mounted, setMounted] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const total = (data || []).reduce((s, d) => s + (d[valueKey] || 0), 0);

  if (!data || data.length === 0 || total <= 0) {
    return <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 12, color: INK.muted }}>داده‌ای برای نمایش نیست</div>;
  }

  const cx = size / 2, cy = size / 2;
  const rOuter = size / 2 - 6;
  const rInner = rOuter * 0.6;
  const targetSweep = mounted ? TAU : 0;

  let cursor = 0;
  const slices = data.map((d, i) => {
    const value = d[valueKey] || 0;
    const fraction = value / total;
    const start = cursor * targetSweep;
    const end = (cursor + fraction) * targetSweep;
    cursor += fraction;
    const mid = (start + end) / 2;
    return { d, i, value, fraction, start, end, mid, color: CATEGORICAL[i % CATEGORICAL.length] };
  });

  const activeSlice = active != null ? slices[active] : null;
  const popOffset = 8;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} onClick={() => setActive(null)}>
          {slices.map((s) => {
            const isActive = active === s.i;
            const ox = isActive ? Math.sin(s.mid) * popOffset : 0;
            const oy = isActive ? -Math.cos(s.mid) * popOffset : 0;
            return (
              <path
                key={s.d[labelKey] ?? s.i}
                d={arcPath(cx, cy, rOuter, rInner, s.start, s.end)}
                fill={s.color}
                stroke="#fcfcfb"
                strokeWidth={2}
                style={{
                  transform: `translate(${ox}px, ${oy}px)`,
                  transformOrigin: `${cx}px ${cy}px`,
                  transition: 'transform 0.2s ease, d 0.6s cubic-bezier(.22,1,.36,1)',
                  cursor: 'pointer',
                  filter: isActive ? 'brightness(1.06)' : 'none',
                }}
                onClick={(e) => { e.stopPropagation(); setActive(active === s.i ? null : s.i); }}
              >
                <title>{`${s.d[labelKey]}: ${formatValue(s.value)} (${Math.round(s.fraction * 100)}%)`}</title>
              </path>
            );
          })}
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', textAlign: 'center' }}>
          {activeSlice ? (
            <>
              <span style={{ fontSize: 11, color: INK.secondary, fontWeight: 600 }}>{activeSlice.d[labelKey]}</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: activeSlice.color }}>{Math.round(activeSlice.fraction * 100)}%</span>
              <span style={{ fontSize: 10, color: INK.muted }}>{formatValue(activeSlice.value)}</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 20, fontWeight: 800, color: INK.primary }}>{formatValue(total)}</span>
              <span style={{ fontSize: 10, color: INK.muted }}>مجموع</span>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 120 }}>
        {slices.map((s) => (
          <button key={s.d[labelKey] ?? s.i} onClick={() => setActive(active === s.i ? null : s.i)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer',
              padding: '3px 0', fontFamily: 'inherit', textAlign: 'right', opacity: active != null && active !== s.i ? 0.5 : 1,
              transition: 'opacity 0.15s',
            }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: INK.primary, fontWeight: active === s.i ? 700 : 500, flex: 1 }}>{s.d[labelKey]}</span>
            <span style={{ fontSize: 11, color: INK.secondary, fontWeight: 600 }}>{formatValue(s.value)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
